import { z } from 'zod'

import { httpClient, type HttpClient } from '../core'
import { generationVersionsEndpoints } from './generation-versions.endpoints'
import { existingTemplateResponseSchema, generateImageResponseSchema, generationVersionListSchema, generationVersionSchema, regenerateResponseSchema, type ExistingTemplateResponseDto, type GenerateImageResponseDto, type GenerationVersionDto, type RegenerateBody, type RegenerateResponseDto, type UpdateAnalysisBody, type UpdateVersionStatusBody } from './generation-versions.schemas'

export class GenerationVersionsService {
  private readonly http: HttpClient

  constructor(http: HttpClient = httpClient) {
    this.http = http
  }

  /** The project's generated versions, newest first. */
  async list(projectId: string, signal?: AbortSignal): Promise<GenerationVersionDto[]> {
    const items = await this.http.get(generationVersionsEndpoints.collection(projectId), generationVersionListSchema, { signal })
    return items.toSorted((a, b) => b.versionNumber - a.versionNumber)
  }

  /**
   * Asks the server to make the project's design. It sends nothing; the server works from what it already holds. It is never repeated
   * by itself: a lost answer may still have started the work, and the server says `alreadyRunning` if asked again.
   */
  generateImage(projectId: string, signal?: AbortSignal): Promise<GenerateImageResponseDto> {
    return this.http.post(generationVersionsEndpoints.generateImage(projectId), generateImageResponseSchema, { signal })
  }

  /**
   * Asks the server for the next version, from what the person wrote. Never repeated by itself: a lost answer may still have started the
   * work, and a second request would make a second version.
   */
  regenerate(projectId: string, body: RegenerateBody, signal?: AbortSignal): Promise<RegenerateResponseDto> {
    return this.http.post(generationVersionsEndpoints.regenerate(projectId), regenerateResponseSchema, { body, signal })
  }

  /** Replaces the analyses sent (the art content and/or the creative brief) of one version. What the answer holds is not used: the caller keeps what it sent. */
  async updateAnalysis(projectId: string, versionId: string, body: UpdateAnalysisBody, signal?: AbortSignal): Promise<void> {
    await this.http.put(generationVersionsEndpoints.item(projectId, versionId), z.unknown(), { body, signal })
  }

  /**
   * Starts an existing-template project from a template kept in a collection: the id of that file (`assetId`, from the collection's list)
   * is all that is sent, as JSON. The answer holds the project and its first version (`generationVersion`), with the template as its picture, ready to be changed with the
   * AI and sent for review. Not retried by itself: a lost answer may still have worked.
   */
  createFromExistingTemplate(projectId: string, input: { assetId: string }, signal?: AbortSignal): Promise<ExistingTemplateResponseDto> {
    return this.http.post(generationVersionsEndpoints.existingTemplate(projectId), existingTemplateResponseSchema, { body: { assetId: input.assetId }, signal, timeoutMs: 5 * 60_000 })
  }

  /** Sets one version's status (sent for review, changes requested, approved). The server decides what that means for the others. */
  async updateStatus(projectId: string, versionId: string, body: UpdateVersionStatusBody, signal?: AbortSignal): Promise<void> {
    await this.http.put(generationVersionsEndpoints.item(projectId, versionId), z.unknown(), { body, signal })
  }

  /** One generated picture, from the storage path a version names in `generatedImageUrls`. */
  downloadImage(projectId: string, path: string, signal?: AbortSignal): Promise<Blob> {
    return this.http.download(generationVersionsEndpoints.download(projectId), { query: { path, download: false }, signal })
  }
}

export const generationVersionsService = new GenerationVersionsService()
